import React, { useCallback } from 'react';
import styled from 'styled-components';
import { PaymentElement } from '@stripe/react-stripe-js';
import {
  StripeError,
  StripePaymentElementChangeEvent,
} from '@stripe/stripe-js';

import ShimmerLoader from 'components/shared/ShimmerLoader/ShimmerLoader';
import { tokens } from '@companycam/slab-tokens';
import { Button, Text } from '@companycam/slab-web';
import { useCurrentUser } from 'javascripts/hooks';
import { translate } from 'javascripts/translations/i18n';
import { PaymentSource } from '../types';
import { APPLE_PAY, GOOGLE_PAY } from './Constants';

const CardContainer = styled.div`
  padding: var(--cc_size_spacing_l);
  display: flex;
  flex: 1;
  flex-direction: row;
  justify-content: space-between;
  background-color: var(--cc_color_background_1);
  border: var(--cc_size_border_width_s) solid var(--cc_color_border_default);
  border-radius: var(--cc_size_border_radius_l);
  height: fit-content;
  align-items: center;
`;

const CardInfoContainer = styled.div`
  display: flex;
`;

const StyledIcon = styled.i`
  font-size: var(--cc_size_text_xl);
  color: var(--cc_color_brand_secondary);
  margin-right: var(--cc_size_spacing_s);
`;

const StripeFormContainer = styled.div`
  width: 100%;
  background-color: var(--cc_color_background_1);
  padding: var(--cc_size_spacing_l);
  border: var(--cc_size_border_width_s) solid var(--cc_color_border_default);
  border-radius: var(--cc_size_border_radius_l);
`;

function PaymentSourceForm({
  paymentSource,
  loadingPaymentSource,
  setFormFilled,
  setIsWalletPayment,
  showPaymentSource,
  setShowPaymentSource,
  handleSubmit,
  handleStripeFailure,
}: {
  paymentSource: PaymentSource;
  loadingPaymentSource: boolean;
  setFormFilled: (arg: boolean) => void;
  setIsWalletPayment: (arg: boolean) => void;
  showPaymentSource: boolean;
  setShowPaymentSource: (arg: boolean) => void;
  handleSubmit: () => void;
  handleStripeFailure: (arg: { error: StripeError }) => void;
}) {
  const user = useCurrentUser();

  const updateFormFilled = useCallback(
    (formData: StripePaymentElementChangeEvent) => {
      setFormFilled(formData.complete);
      const paymentType = formData.value?.type;
      setIsWalletPayment(
        paymentType === GOOGLE_PAY || paymentType === APPLE_PAY,
      );
    },
    [setFormFilled, setIsWalletPayment],
  );

  if (loadingPaymentSource) {
    return (
      <StripeFormContainer>
        <ShimmerLoader height={tokens.size_text_m} />
      </StripeFormContainer>
    );
  }

  if (showPaymentSource)
    return (
      <CardContainer>
        <CardInfoContainer>
          <StyledIcon className="mdi mdi-credit-card-outline" />
          <Text as="p" family="heading" size="m" ccMargin="none" weight={700}>
            {translate('billing.stripePaymentModal.paymentSource', {
              paymentSourceBrand: paymentSource.brand || paymentSource.bankName,
              paymentSourceLast4: paymentSource.last4,
            })}
          </Text>
        </CardInfoContainer>
        <Button
          color="primary"
          design="minimal"
          onClick={() => setShowPaymentSource(false)}
        >
          {translate('common.update')}
        </Button>
      </CardContainer>
    );
  return (
    <StripeFormContainer>
      <form id="payment-form" onSubmit={handleSubmit}>
        <PaymentElement
          id="payment-element"
          onChange={updateFormFilled}
          options={{
            defaultValues: {
              billingDetails: { email: user.email_address },
            },
          }}
          onLoadError={handleStripeFailure}
        />
      </form>
    </StripeFormContainer>
  );
}

export default PaymentSourceForm;
